import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import useDropdownPlacement from '../hooks/useDropdownPlacement';
import { markDialogDirty } from '../hooks/useDialog';

const LIST_MAX = 320; // matches the old max-h-80
const LIST_MIN = 160;
const ROW_H = 38;
const TYPEAHEAD_MS = 800;

// Country codes with ISO 3166-1 alpha-2 for flag lookup via flagcdn.com
// Common destinations for a Pakistani clinic — PK first
export const COUNTRY_CODES = [
  { code: '92',  iso: 'pk', name: 'Pakistan' },
  { code: '971', iso: 'ae', name: 'UAE' },
  { code: '966', iso: 'sa', name: 'Saudi Arabia' },
  { code: '44',  iso: 'gb', name: 'United Kingdom' },
  { code: '1',   iso: 'us', name: 'USA / Canada' },
  { code: '974', iso: 'qa', name: 'Qatar' },
  { code: '968', iso: 'om', name: 'Oman' },
  { code: '965', iso: 'kw', name: 'Kuwait' },
  { code: '973', iso: 'bh', name: 'Bahrain' },
  { code: '61',  iso: 'au', name: 'Australia' },
  { code: '49',  iso: 'de', name: 'Germany' },
  { code: '60',  iso: 'my', name: 'Malaysia' },
  { code: '91',  iso: 'in', name: 'India' },
  { code: '86',  iso: 'cn', name: 'China' },
];

const flagUrl = (iso) => `https://flagcdn.com/w20/${iso}.png`;
const flagUrl2x = (iso) => `https://flagcdn.com/w40/${iso}.png 2x`;

/**
 * Custom country code dropdown that renders actual flag images (from flagcdn.com).
 * Native <select> can't render images, so we build a small popover dropdown.
 *
 * Keyboard handling follows the shared Select: focus stays on the button while the list is open.
 * ArrowDown, ArrowUp, Enter or Space opens it on the current country; the arrow keys, Home and End
 * move a highlight; typing jumps (digits match the dialling code, so 9 then 7 then 1 reaches UAE,
 * and letters match the name); Enter or Space picks; Escape closes and Tab closes and moves on to
 * the number. It used to have no keyboard handling at all.
 *
 * Props:
 *   value: string — selected country code (e.g. "92")
 *   onChange: (newCode: string) => void
 *   className: optional extra classes for the trigger button
 */
const CountryCodeSelect = ({ value, onChange, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const typed = useRef({ text: '', at: 0 });

  const selected = COUNTRY_CODES.find(c => c.code === value) || COUNTRY_CODES[0];

  useEffect(() => {
    if (!open) return undefined;
    setHighlight(Math.max(0, COUNTRY_CODES.findIndex(c => c.code === value)));
    const onMouseDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    // Taken in the capture phase and stopped, like the shared Select, so Escape closes this list and
    // not the dialog the phone field sits in.
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep the highlighted country in view in a list taller than its panel.
  useEffect(() => {
    if (!open || highlight < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${highlight}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [open, highlight]);

  const { dropUp, space } = useDropdownPlacement(open, wrapperRef, {
    wantHeight: Math.min(COUNTRY_CODES.length * ROW_H, LIST_MAX),
    minHeight: LIST_MIN,
  });

  const pick = (country, fromKeyboard) => {
    if (country.code !== value) markDialogDirty(wrapperRef.current);
    onChange(country.code);
    setOpen(false);
    if (fromKeyboard) buttonRef.current?.focus();
  };

  // Keys typed within a short pause build one search, so "97" can go past 9 to 971.
  const findTyped = (key) => {
    const now = Date.now();
    const buffer = now - typed.current.at > TYPEAHEAD_MS ? key : typed.current.text + key;
    typed.current = { text: buffer, at: now };
    const q = buffer.toLowerCase();
    return /^\d+$/.test(q)
      ? COUNTRY_CODES.findIndex(c => c.code.startsWith(q))
      : COUNTRY_CODES.findIndex(c => c.name.toLowerCase().startsWith(q));
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen(true);
      }
      // Enter and Space open it through the button's own click.
      return;
    }
    const last = COUNTRY_CODES.length - 1;
    if (e.key === 'Tab') {
      // Focus is already on the button, so the browser's Tab moves on to the number field.
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, last));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setHighlight(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setHighlight(last);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (COUNTRY_CODES[highlight]) pick(COUNTRY_CODES[highlight], true);
    } else if (/^[a-z0-9]$/i.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const idx = findTyped(e.key);
      if (idx >= 0) {
        e.preventDefault();
        setHighlight(idx);
      }
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(!open)}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex items-center gap-1.5 px-2.5 h-full bg-gray-50 hover:bg-gray-100 border-r border-gray-300 text-gray-700 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500 transition-colors ${className}`}
        aria-label={`Country code: +${selected.code} ${selected.name}`}
      >
        <img
          src={flagUrl(selected.iso)}
          srcSet={flagUrl2x(selected.iso)}
          alt={selected.name}
          width="20"
          height="15"
          className="rounded-sm shadow-sm"
        />
        <span className="text-sm">+{selected.code}</span>
        <ChevronDown className={`h-3.5 w-3.5 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          ref={listRef}
          role="listbox"
          className={`absolute left-0 w-64 overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-200 py-1 z-30
            ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}
          style={{ maxHeight: Math.max(LIST_MIN, Math.min(LIST_MAX, space)) }}
        >
          {COUNTRY_CODES.map((c, i) => {
            const isSelected = c.code === value;
            const isHighlighted = highlight === i;
            return (
              <button
                type="button"
                key={c.code}
                role="option"
                aria-selected={isSelected}
                tabIndex={-1}
                data-idx={i}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(c, false)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                  isHighlighted
                    ? 'bg-emerald-600 text-white'
                    : isSelected ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700'
                }`}
              >
                <img
                  src={flagUrl(c.iso)}
                  srcSet={flagUrl2x(c.iso)}
                  alt=""
                  width="20"
                  height="15"
                  className="rounded-sm shadow-sm flex-shrink-0"
                />
                <span className="flex-1 text-sm font-medium truncate">{c.name}</span>
                <span className={`text-sm font-mono ${isHighlighted ? 'text-emerald-50' : 'text-gray-500'}`}>+{c.code}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CountryCodeSelect;
